import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './Testimonials.css';

const Testimonials = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonialData = t('testimonials.items', { returnObjects: true });
  const testimonials = testimonialData.map((item, index) => ({
    ...item,
    image: `https://i.pravatar.cc/150?img=${[12, 45, 33][index]}`,
    rating: 5
  }));

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="testimonials-section">
      <div className="testimonials-container">
        <motion.div
          className="testimonials-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="testimonials-title">{t('testimonials.title')}</h2>
          <p className="testimonials-subtitle">{t('testimonials.subtitle')}</p>
        </motion.div>

        <div className="testimonials-stack">
          {testimonials.map((testimonial, index) => {
            const offset = index - currentIndex;
            const isVisible = Math.abs(offset) <= 1;

            return (
              <motion.div
                key={index}
                className={`testimonial-card ${
                  offset === 0 ? 'active' : offset === 1 || offset === -2 ? 'next' : 'prev'
                }`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{
                  opacity: isVisible ? 1 : 0,
                  scale: offset === 0 ? 1 : 0.9,
                  x: '-50%',
                  y: offset === 0 ? '-50%' : `calc(-50% + ${offset * 20}px)`,
                  zIndex: testimonials.length - Math.abs(offset),
                  rotateY: offset * 5
                }}
                transition={{ duration: 0.5 }}
                style={{
                  display: isVisible ? 'block' : 'none',
                  left: '50%',
                  top: '50%'
                }}
              >
                <Quote className="quote-icon" size={48} />
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} fill="var(--gold)" color="var(--gold)" />
                  ))}
                </div>
                <p className="testimonial-text">{testimonial.text}</p>
                <div className="testimonial-author">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="author-image"
                  />
                  <div className="author-info">
                    <h4 className="author-name">{testimonial.name}</h4>
                    <p className="author-role">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="testimonials-controls">
          <button className="control-button" onClick={prevTestimonial} aria-label="Previous">
            <ChevronLeft size={24} />
          </button>
          <div className="testimonials-indicators">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
          <button className="control-button" onClick={nextTestimonial} aria-label="Next">
            <ChevronRight size={24} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
